import React, { useState, useEffect } from "react";
import { VideoMeta } from "@/types/video";
import { getVideoMetadataByVid } from "@/services/firebase/byteplus";

interface VideoFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<VideoMeta, "id" | "createdAt" | "updatedAt">) => Promise<void>;
  video?: VideoMeta & { id: string };
  dramaId: string;
  defaultOrder?: number;
  /** drama_video_orientation：0=直式 / 1=橫式。傳入後可在「從 BytePlus 帶入」時比對影片實際方向是否相符。 */
  dramaOrientation?: number;
  /** 劇層單集解鎖價（dramas/{id}.episode_unlock_coin_cost）。用於「留空＝沿用全劇 X」提示。 */
  dramaEpisodeUnlockCost?: number | null;
}

// 將「台灣時間」(datetime-local 格式) 轉換為 UTC Date（不依賴本機時區）
function taipeiToUTC(taipeiDateTimeLocal: string): Date {
  // datetime-local 格式：YYYY-MM-DDTHH:mm
  // 注意：new Date("YYYY-MM-DDTHH:mm") 會以「本機時區」解析，
  // 若本機本來就是台北時區，再手動 -8 小時會造成重複偏移。
  // 這裡改成「手動解析欄位」並固定視為 Asia/Taipei (UTC+8)。
  const [datePart, timePart] = taipeiDateTimeLocal.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);

  // 先把輸入當成「台灣時間」的年月日時分，轉成 UTC：
  // UTC = 台灣時間 - 8 小時
  const utcMillis = Date.UTC(year, month - 1, day, hour, minute, 0) - 8 * 60 * 60 * 1000;
  return new Date(utcMillis);
}

// 將 UTC Date 轉換為台灣時間字串（datetime-local 格式）
function utcToTaipeiLocal(utcDate: Date | undefined): string {
  if (!utcDate) return '';
  // UTC + 8 小時 = 台灣時間
  const taipeiDate = new Date(utcDate.getTime() + 8 * 60 * 60 * 1000);
  // 轉換為 datetime-local 格式：YYYY-MM-DDTHH:mm
  const year = taipeiDate.getUTCFullYear();
  const month = String(taipeiDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(taipeiDate.getUTCDate()).padStart(2, '0');
  const hours = String(taipeiDate.getUTCHours()).padStart(2, '0');
  const minutes = String(taipeiDate.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// 格式化台灣時間顯示字串
function formatTaipeiDisplay(utcDate: Date | undefined): string {
  if (!utcDate) return '';
  const taipeiDate = new Date(utcDate.getTime() + 8 * 60 * 60 * 1000);
  const year = taipeiDate.getUTCFullYear();
  const month = taipeiDate.getUTCMonth() + 1;
  const day = taipeiDate.getUTCDate();
  const hours = String(taipeiDate.getUTCHours()).padStart(2, '0');
  const minutes = String(taipeiDate.getUTCMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}`;
}

export default function VideoFormDialog({
  isOpen,
  onClose,
  onSave,
  video,
  dramaId,
  defaultOrder = 1,
  dramaOrientation,
  dramaEpisodeUnlockCost,
}: VideoFormDialogProps) {
  const [formData, setFormData] = useState<Omit<VideoMeta, "id" | "createdAt" | "updatedAt">>({
    vid: "",
    drama_id: dramaId,
    order: defaultOrder,
    vip: false,
    episode_unlock_coin_cost: undefined,
    episode_type: "normal",
    episode_label: "",
    width: undefined,
    height: undefined,
    caption: "",
    name: "",
    duration: undefined,
    cover_url: "",
    display_type: 0,
    play_times: 0,
    release_status: "not_scheduled",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [localDateTimeValue, setLocalDateTimeValue] = useState<string>('');
  // 從 BytePlus 自動帶入 metadata
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [fetchMetaError, setFetchMetaError] = useState<string | null>(null);
  const [fetchMetaSuccess, setFetchMetaSuccess] = useState<string | null>(null);
  const [fetchMetaWarnings, setFetchMetaWarnings] = useState<string[]>([]);

  const handleFetchMetadata = async () => {
    const vid = formData.vid.trim();
    if (!vid) {
      setFetchMetaError("請先填入 vid");
      setFetchMetaSuccess(null);
      setFetchMetaWarnings([]);
      return;
    }
    setFetchingMeta(true);
    setFetchMetaError(null);
    setFetchMetaSuccess(null);
    setFetchMetaWarnings([]);
    try {
      const meta = await getVideoMetadataByVid(vid, dramaOrientation);
      setFormData((prev) => ({
        ...prev,
        vid: meta.vid || prev.vid,
        width: meta.width || prev.width,
        height: meta.height || prev.height,
        duration: meta.duration || prev.duration,
        cover_url: meta.cover_url || prev.cover_url,
        // 若 admin 還沒填 name，順便用 BytePlus title 當預設
        name: prev.name || meta.title || prev.name,
      }));
      // 清掉相關 errors
      setErrors((prev) => {
        const next = { ...prev };
        delete next.width;
        delete next.height;
        delete next.duration;
        return next;
      });
      const filled: string[] = [];
      if (meta.width) filled.push(`寬 ${meta.width}`);
      if (meta.height) filled.push(`高 ${meta.height}`);
      if (meta.duration) filled.push(`時長 ${meta.duration.toFixed(2)}s`);
      // cover_url 仍會寫進 Firestore，但 UI 暫時不顯示（BytePlus image domain 缺 SSL）
      setFetchMetaSuccess(
        filled.length > 0
          ? `已帶入：${filled.join("、")}`
          : "已連到 BytePlus，但此 vid 尚無 metadata（可能仍在轉碼）"
      );

      // 收集 server 端 warnings + 前端方向比對
      const warnings = [...(meta.validation?.warnings || [])];
      if (
        typeof dramaOrientation === "number" &&
        meta.width > 0 &&
        meta.height > 0
      ) {
        const videoIsVertical = meta.height >= meta.width; // >=：正方形視為直式，與 server（byteplus.ts）判定一致
        const dramaIsVertical = dramaOrientation === 0;
        if (videoIsVertical !== dramaIsVertical) {
          warnings.push(
            `劇集設定為「${dramaIsVertical ? "直式" : "橫式"}」，` +
              `但此 vid 是「${videoIsVertical ? "直式" : "橫式"}」（${meta.width}×${meta.height}），` +
              "App 顯示會變形 — 請確認劇集方向或換正確比例的影片"
          );
        }
      }
      setFetchMetaWarnings(warnings);
    } catch (e: any) {
      setFetchMetaError(e?.message || "取得 metadata 失敗");
    } finally {
      setFetchingMeta(false);
    }
  };

  // vid 格式檢查（即時，輕量提示，不阻擋）
  const vidFormatHint = React.useMemo<string | null>(() => {
    const v = formData.vid;
    if (!v) return null;
    if (v.includes("://") || v.toLowerCase().startsWith("http")) {
      return "看起來像 URL — 請只貼 vid 字串，不要貼整個連結";
    }
    if (/\s/.test(v)) return "vid 含空白字元，可能是複製到多餘內容";
    if (v.length < 20) return "vid 偏短（一般 BytePlus vid 長度約 32 字元）";
    if (v.length > 50) return "vid 偏長，可能複製到多餘內容";
    return null;
  }, [formData.vid]);

  // 當 vid 已填入時，寬/高/時長 強制走「從 BytePlus 帶入」，避免手動改錯
  // （之前踩過坑：admin 把 1080x1920 直式改成 1920x1080 橫式造成 App 變形）
  const metadataLocked = !!formData.vid && formData.vid.trim().length > 0;

  useEffect(() => {
    if (video) {
      setFormData({
        vid: video.vid || "",
        drama_id: video.drama_id || dramaId,
        order: video.order || 1,
        vip: video.vip || false,
        episode_unlock_coin_cost: video.episode_unlock_coin_cost ?? undefined,
        episode_type: video.episode_type ?? "normal",
        episode_label: video.episode_label ?? "",
        width: video.width,
        height: video.height,
        caption: video.caption || "",
        name: video.name || "",
        duration: video.duration,
        cover_url: video.cover_url || "",
        display_type: video.display_type ?? 0,
        play_times: video.play_times || 0,
        scheduled_release_time: video.scheduled_release_time,
        scheduled_release_timezone: video.scheduled_release_timezone,
        scheduled_release_time_display: video.scheduled_release_time_display,
        is_released: video.is_released,
        release_status: video.release_status || "not_scheduled",
      });
      const localValue = utcToTaipeiLocal(video.scheduled_release_time);
      setLocalDateTimeValue(localValue);
    } else {
      setFormData({
        vid: "",
        drama_id: dramaId,
        order: defaultOrder,
        vip: false,
        episode_unlock_coin_cost: undefined,
        episode_label: "",
        width: undefined,
        height: undefined,
        caption: "",
        name: "",
        duration: undefined,
        cover_url: "",
        display_type: 0,
        play_times: 0,
        release_status: "not_scheduled",
      });
      setLocalDateTimeValue('');
    }
    setErrors({});
  }, [video, dramaId, defaultOrder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 驗證
    const newErrors: Record<string, string> = {};
    if (!formData.vid.trim()) {
      newErrors.vid = "影片 ID 為必填";
    }
    if (!formData.order || formData.order < 1) {
      newErrors.order = "播放順序必須大於 0";
    }
    if (!formData.width || formData.width <= 0) {
      newErrors.width = "寬度為必填（需大於 0）";
    }
    if (!formData.height || formData.height <= 0) {
      newErrors.height = "高度為必填（需大於 0）";
    }
    if (formData.duration === undefined || formData.duration === null || formData.duration <= 0) {
      newErrors.duration = "時長為必填（秒，需大於 0）";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      // Firestore updateDoc()/addDoc() 不接受 undefined，需先移除以避免儲存失敗
      const cleanedData: any = { ...formData };
      // 免費集（vip=false）不需要集級覆寫價，顯式清除（null → service deleteField）
      if (!cleanedData.vip) {
        cleanedData.episode_unlock_coin_cost = null;
      }
      for (const key of Object.keys(cleanedData)) {
        if (cleanedData[key] === undefined) {
          delete cleanedData[key];
        }
      }

      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error("Error saving video:", error);
      alert("保存失敗：" + (error instanceof Error ? error.message : "未知錯誤"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-surface-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-white dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-surface-900 dark:text-white">
            {video ? "編輯影集" : "新增影集"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 text-surface-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                影片 ID (VID) <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.vid}
                  onChange={(e) => {
                    // 自動 trim 兩端空白（複製常見會帶換行/空格）
                    setFormData({ ...formData, vid: e.target.value.trim() });
                    setFetchMetaError(null);
                    setFetchMetaSuccess(null);
                    setFetchMetaWarnings([]);
                  }}
                  className={`flex-1 px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white ${
                    errors.vid ? "border-red-500" : "border-surface-300 dark:border-surface-600"
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="例如: v002e5g00000... （從 BytePlus 後台複製）"
                />
                <button
                  type="button"
                  onClick={handleFetchMetadata}
                  disabled={fetchingMeta || !formData.vid.trim()}
                  className="shrink-0 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium"
                  title="呼叫 BytePlus API 自動帶入 寬/高/時長/封面"
                >
                  {fetchingMeta && (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                  )}
                  {fetchingMeta ? "查詢中…" : "從 BytePlus 帶入"}
                </button>
              </div>
              {errors.vid && (
                <p className="mt-1 text-sm text-red-500">{errors.vid}</p>
              )}
              {vidFormatHint && !fetchMetaSuccess && (
                <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">⚠️ {vidFormatHint}</p>
              )}
              {fetchMetaError && (
                <p className="mt-1 text-sm text-red-500">⚠️ {fetchMetaError}</p>
              )}
              {fetchMetaSuccess && (
                <p className="mt-1 text-sm text-emerald-600 dark:text-emerald-400">✓ {fetchMetaSuccess}</p>
              )}
              {fetchMetaWarnings.length > 0 && (
                <div className="mt-2 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2.5 text-sm">
                  <div className="font-medium text-amber-800 dark:text-amber-200 mb-1">⚠️ 影片檢查發現問題：</div>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-800 dark:text-amber-200">
                    {fetchMetaWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                播放順序 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={formData.order}
                onChange={(e) =>
                  setFormData({ ...formData, order: parseInt(e.target.value) || 1 })
                }
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white ${
                  errors.order ? "border-red-500" : "border-surface-300 dark:border-surface-600"
                } focus:outline-none focus:ring-2 focus:ring-primary`}
              />
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                播放位置（1、2、3…全劇唯一、連續）。特番照樣插在正片之間，例如正片 1
                之後接 SP 就填 2、下一個正片填 3。顯示集數由「集型」自動算，不是這個值。
              </p>
              {errors.order && (
                <p className="mt-1 text-sm text-red-500">{errors.order}</p>
              )}
            </div>

            {/* 寬/高/時長：vid 有填時 readonly，強制走「從 BytePlus 帶入」避免手改錯
                （之前踩過坑：admin 把 1080×1920 改成 1920×1080 造成 App 顯示變形） */}
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                寬度 (px) <span className="text-red-500">*</span>
                {metadataLocked && (
                  <span className="ml-2 text-xs font-normal text-surface-500">🔒 由 BytePlus 帶入</span>
                )}
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.width ?? ""}
                readOnly={metadataLocked}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    width: e.target.value === "" ? undefined : parseInt(e.target.value, 10),
                  })
                }
                className={`w-full px-4 py-2 border rounded-lg text-surface-900 dark:text-white ${
                  errors.width ? "border-red-500" : "border-surface-300 dark:border-surface-600"
                } ${
                  metadataLocked
                    ? "bg-surface-100 dark:bg-surface-900/40 cursor-not-allowed text-surface-500"
                    : "bg-white dark:bg-surface-700 focus:outline-none focus:ring-2 focus:ring-primary"
                }`}
                placeholder={metadataLocked ? "" : "例如: 1080"}
              />
              {errors.width && <p className="mt-1 text-sm text-red-500">{errors.width}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                高度 (px) <span className="text-red-500">*</span>
                {metadataLocked && (
                  <span className="ml-2 text-xs font-normal text-surface-500">🔒 由 BytePlus 帶入</span>
                )}
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.height ?? ""}
                readOnly={metadataLocked}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    height: e.target.value === "" ? undefined : parseInt(e.target.value, 10),
                  })
                }
                className={`w-full px-4 py-2 border rounded-lg text-surface-900 dark:text-white ${
                  errors.height ? "border-red-500" : "border-surface-300 dark:border-surface-600"
                } ${
                  metadataLocked
                    ? "bg-surface-100 dark:bg-surface-900/40 cursor-not-allowed text-surface-500"
                    : "bg-white dark:bg-surface-700 focus:outline-none focus:ring-2 focus:ring-primary"
                }`}
                placeholder={metadataLocked ? "" : "例如: 1920"}
              />
              {errors.height && <p className="mt-1 text-sm text-red-500">{errors.height}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                時長 (秒) <span className="text-red-500">*</span>
                {metadataLocked && (
                  <span className="ml-2 text-xs font-normal text-surface-500">🔒 由 BytePlus 帶入</span>
                )}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.duration ?? ""}
                readOnly={metadataLocked}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    duration: e.target.value === "" ? undefined : Number(e.target.value),
                  })
                }
                className={`w-full px-4 py-2 border rounded-lg text-surface-900 dark:text-white ${
                  errors.duration ? "border-red-500" : "border-surface-300 dark:border-surface-600"
                } ${
                  metadataLocked
                    ? "bg-surface-100 dark:bg-surface-900/40 cursor-not-allowed text-surface-500"
                    : "bg-white dark:bg-surface-700 focus:outline-none focus:ring-2 focus:ring-primary"
                }`}
                placeholder={metadataLocked ? "" : "例如: 65.5"}
              />
              {errors.duration && <p className="mt-1 text-sm text-red-500">{errors.duration}</p>}
              {metadataLocked ? (
                <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                  如需手動編輯，請先清空上方的 vid 欄位。
                </p>
              ) : (
                <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                  單位為「秒」，App 端會用 duration × 1000 當毫秒。
                </p>
              )}
            </div>

            {/* 顯示型態：admin 流程上幾乎不會用到（強化資訊卡是 App 端 UI 變體），預設 0=一般。
                若日後需要 admin 控制，移除外層 false 即可。 */}
            {false && (
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  顯示型態 (display_type)
                </label>
                <select
                  value={formData.display_type ?? 0}
                  onChange={(e) => setFormData({ ...formData, display_type: parseInt(e.target.value, 10) })}
                  className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value={0}>0 - 一般</option>
                  <option value={1}>1 - 強化資訊卡/導流樣式</option>
                </select>
              </div>
            )}

            {/* 封面圖 (cover_url) 欄位暫時隱藏：BytePlus image domain 尚未配 SSL，
                客戶決定不付費購 cert，CMS / App 端的 cover URL 暫時無法載入。
                資料層仍會由 BytePlus callback / 「從 BytePlus 帶入」自動寫入 Firestore，
                未來 SSL 配好後此欄位即可恢復顯示。 */}
            {false && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  封面圖 (cover_url)
                </label>
                <input
                  type="text"
                  value={formData.cover_url ?? ""}
                  onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="建議填入完整 URL（https://...）"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                影集標題 (name)
              </label>
              <input
                type="text"
                value={formData.name ?? ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="例如：靈魂互換的開始"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                集型
              </label>
              <select
                value={formData.episode_type ?? "normal"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    episode_type: e.target.value as VideoMeta["episode_type"],
                  })
                }
                className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="normal">正片</option>
                <option value="special">特番 / SP</option>
              </select>
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                決定 App 自動編號：正片＝第 1、2、3…集；特番＝SP1、SP2…（不佔正片集數）。
                排序仍由「集數順序（order）」決定，特番照樣插在正片之間。
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                選集顯示標籤（選填覆寫）
              </label>
              <input
                type="text"
                maxLength={6}
                value={formData.episode_label ?? ""}
                onChange={(e) => setFormData({ ...formData, episode_label: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="留空＝依集型自動編號"
              />
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                <span className="font-medium">只影響 App 選集選單格子</span>
                的顯示文字；player 左上與觀看歷史一律用集型自動編號（第 N 集 / SP N）。
                一般留空即可，自動編號就夠用。
              </p>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                影集描述 (caption)
              </label>
              <textarea
                value={formData.caption ?? ""}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="可選填"
                rows={3}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                上線時間設定
              </label>
              <input
                type="datetime-local"
                value={localDateTimeValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setLocalDateTimeValue(newValue);
                  if (newValue) {
                    const utcDate = taipeiToUTC(newValue);
                    const now = new Date();
                    const isReleased = utcDate <= now;
                    const displayStr = formatTaipeiDisplay(utcDate);
                    
                    setFormData({
                      ...formData,
                      scheduled_release_time: utcDate,
                      scheduled_release_timezone: 'Asia/Taipei',
                      scheduled_release_time_display: displayStr,
                      is_released: isReleased,
                      release_status: isReleased ? 'released' : 'scheduled',
                    });
                  } else {
                    // 清除上線時間
                    setFormData({
                      ...formData,
                      scheduled_release_time: undefined,
                      scheduled_release_timezone: undefined,
                      scheduled_release_time_display: undefined,
                      is_released: false,
                      release_status: 'not_scheduled',
                    });
                  }
                }}
                className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="mt-2 space-y-1">
                {formData.scheduled_release_time && (
                  <p className="text-xs text-surface-500 dark:text-surface-400">
                    上線時間：{formData.scheduled_release_time_display || formatTaipeiDisplay(formData.scheduled_release_time)}
                  </p>
                )}
                {formData.release_status && (
                  <div>
                    {formData.release_status === "scheduled" && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-info text-info-foreground">
                        已排程
                      </span>
                    )}
                    {formData.release_status === "released" && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success text-success-foreground">
                        已上線
                      </span>
                    )}
                    {formData.release_status === "not_scheduled" && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-neutral text-neutral-foreground">
                        未排程
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-surface-200 dark:border-surface-700 p-3 bg-surface-50 dark:bg-surface-900/40">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.vip || false}
                onChange={(e) => setFormData({ ...formData, vip: e.target.checked })}
                className="mt-0.5 w-4 h-4 text-primary rounded focus:ring-primary"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-surface-800 dark:text-surface-200">
                    🔒 解鎖設定
                  </span>
                  {formData.vip ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                      需解鎖
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                      免費觀看
                    </span>
                  )}
                </div>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                  勾選後本集需要金幣解鎖；取消勾選則所有用戶可直接免費觀看（適合前幾集引流）
                </p>
              </div>
            </label>

            {formData.vip && (
              <div className="mt-3 pl-7">
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  本集解鎖金幣（集級覆寫）
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.episode_unlock_coin_cost ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      // 空字串 → null（顯式清除、沿用全劇預設）；否則存整數
                      episode_unlock_coin_cost:
                        e.target.value === ""
                          ? null
                          : Math.max(1, parseInt(e.target.value, 10) || 1),
                    })
                  }
                  className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white border-surface-300 dark:border-surface-600 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={
                    typeof dramaEpisodeUnlockCost === "number" && dramaEpisodeUnlockCost > 0
                      ? `留空＝沿用全劇預設 ${dramaEpisodeUnlockCost} 金幣`
                      : "留空＝沿用全劇預設價"
                  }
                />
                <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                  只在「本集價與全劇不同」時填寫（例如 SP/特番 100）。留空則沿用全劇單集價
                  {typeof dramaEpisodeUnlockCost === "number" && dramaEpisodeUnlockCost > 0
                    ? `（目前 ${dramaEpisodeUnlockCost} 金幣）`
                    : ""}
                  。
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-surface-200 dark:border-surface-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 rounded-lg hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isSaving ? "保存中..." : "保存"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

